import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  MenuBook as JournalIcon,
  DeleteOutline as DeleteIcon,
  TextFields as TextFieldsIcon,
  NotificationsActive as ReminderIcon,
  PhotoCamera as PhotoCameraIcon,
  GraphicEq as AudioRecordIcon,
  KeyboardVoice as SpeechIcon,
  Link as LinkIcon,
  Stop as StopIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Image as ImageIcon,
  Edit as EditIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  CheckCircleOutline as DoneIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
} from "@mui/icons-material";
import AddressbookPanelShell from "./AddressbookPanelShell";
import Gate from "../../../components/common/Gate";
import {
  apiGetJournals,
  apiCreateJournal,
  apiUpdateJournal,
  apiDeleteJournal,
  apiUpsertJournal,
  apiCancelJournalReminder,
} from "../../../graphQL/journal";
import { apiSendSms } from "../../../graphQL/twilio";
import { JOURNAL_ACTION_EVENT } from "../../../utils/constants";

/* ── helpers ── */

function parseNotes(journal) {
  if (!journal?.data) return [];
  const data = typeof journal.data === "string" ? JSON.parse(journal.data) : journal.data;
  const notes = data?.notes || data?.data?.notes || [];
  return Array.isArray(notes) ? notes : [];
}

function formatTime(ts) {
  if (!ts) return "";
  const d = new Date(typeof ts === "number" ? ts : ts);
  if (isNaN(d)) return "";
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatTimestamp(value) {
  if (value === null || value === undefined || value === "") return "";
  const asString = String(value);
  const isEpochMs = /^\d{10,}$/.test(asString);
  const d = new Date(isEpochMs ? Number(asString) : value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

function setTimeTo8am(date) {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return d;
  d.setHours(8, 0, 0, 0);
  return d;
}

function addDuration(baseDate, amount, unit) {
  const d = new Date(baseDate);
  const n = Number(amount);
  if (!n || n <= 0) return d;
  if (unit === "day") { d.setDate(d.getDate() + n); return d; }
  if (unit === "week") { d.setDate(d.getDate() + n * 7); return d; }
  if (unit === "month") { d.setMonth(d.getMonth() + n); return d; }
  if (unit === "year") { d.setFullYear(d.getFullYear() + n); return d; }
  return d;
}

let _noteId = 0;
function makeNoteId() {
  return `note_${Date.now().toString(36)}_${(++_noteId).toString(36)}`;
}

/* ── constants ── */

const ACTION_OPTIONS = [
  { value: "call", label: "call" },
  { value: "email", label: "email" },
  { value: "text", label: "text" },
  { value: "schedule_meeting", label: "schedule a meeting" },
  { value: "attend_meeting", label: "attend a meeting" },
  { value: "meet_in_person", label: "meet in person" },
];

const STATE_CHIP_STYLES = {
  created:   { borderColor: "#CBD5E1", color: "#475569", background: "#F8FAFC" },
  scheduled: { borderColor: "#F59E0B", color: "#B45309", background: "#FFFBEB" },
  due:       { borderColor: "#22C55E", color: "#166534", background: "#ECFDF5" },
};

const ACCENTS = {
  reminder: { border: "#FDE68A", bg: "#FFFBEB", fg: "#D97706", hover: "#FEF3C7", activeBorder: "#F59E0B", activeBg: "#FDE68A", activeFg: "#92400E", activeHover: "#FCD34D" },
  text:     { border: "#BFDBFE", bg: "#EFF6FF", fg: "#2563EB", hover: "#DBEAFE", activeBorder: "#60A5FA", activeBg: "#BFDBFE", activeFg: "#1D4ED8", activeHover: "#93C5FD" },
  photo:    { border: "#C4B5FD", bg: "#F5F3FF", fg: "#7C3AED", hover: "#EDE9FE", activeBorder: "#C4B5FD", activeBg: "#F5F3FF", activeFg: "#7C3AED", activeHover: "#EDE9FE" },
  audio:    { border: "#FECACA", bg: "#FEF2F2", fg: "#DC2626", hover: "#FEE2E2", activeBorder: "#fca5a5", activeBg: "#fee2e2", activeFg: "#dc2626", activeHover: "#fecaca" },
  link:     { border: "#FBCFE8", bg: "#FDF2F8", fg: "#DB2777", hover: "#FCE7F3", activeBorder: "#F472B6", activeBg: "#FBCFE8", activeFg: "#BE185D", activeHover: "#F9A8D4" },
};

const TYPE_META = {
  text:     { label: "Note",     icon: TextFieldsIcon,  color: "#2563EB" },
  reminder: { label: "Reminder", icon: ReminderIcon,     color: "#D97706" },
  audio:    { label: "Audio",    icon: AudioRecordIcon,  color: "#DC2626" },
  image:    { label: "Photo",    icon: ImageIcon,        color: "#7C3AED" },
  link:     { label: "Link",     icon: LinkIcon,         color: "#DB2777" },
};

const ALL_TYPES = ["text", "reminder", "audio", "image", "link"];

/* ── Action button ── */

/* Compact label shown under each toolbar icon (mirrors the app-bar buttons). */
const TOOLBAR_LABEL_SX = {
  fontSize: 9, lineHeight: 1.1, fontWeight: 500, color: "#6B7280",
  textTransform: "none", whiteSpace: "nowrap", maxWidth: 48,
  overflow: "hidden", textOverflow: "ellipsis",
};

const ActionBtn = ({ title, label, onClick, active, disabled, accent, ActiveIcon, Icon }) => (
  <Tooltip title={title}>
    <Box component="span" sx={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 0.125 }}>
      <IconButton
        size="small"
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        sx={{
          width: 24, height: 24, borderRadius: "50%",
          border: "1px solid",
          borderColor: disabled ? "#E5E7EB" : active ? accent.activeBorder : accent.border,
          backgroundColor: disabled ? "#F3F4F6" : active ? accent.activeBg : accent.bg,
          color: disabled ? "#D1D5DB" : active ? accent.activeFg : accent.fg,
          "&:hover": { backgroundColor: disabled ? "#F3F4F6" : active ? accent.activeHover : accent.hover },
          "&.Mui-disabled": { borderColor: "#E5E7EB", backgroundColor: "#F3F4F6", color: "#D1D5DB" },
        }}
      >
        {ActiveIcon && active ? <ActiveIcon sx={{ fontSize: 13 }} /> : <Icon sx={{ fontSize: 13 }} />}
      </IconButton>
      {label && <Typography sx={TOOLBAR_LABEL_SX}>{label}</Typography>}
    </Box>
  </Tooltip>
);

/* ── Audio player ── */

const AudioPlayer = ({ src }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); } else { audioRef.current.play(); }
    setPlaying(!playing);
  };
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <IconButton size="small" onClick={toggle} sx={{ color: "#DC2626", p: 0.25 }}>
        {playing ? <PauseIcon sx={{ fontSize: 16 }} /> : <PlayIcon sx={{ fontSize: 16 }} />}
      </IconButton>
      <audio ref={audioRef} src={src} onEnded={() => setPlaying(false)} />
      <Typography sx={{ fontSize: 11, color: "#6B7280" }}>Audio recording</Typography>
    </Box>
  );
};

/* ── Text-to-speech (Web Speech API, browser native, zero cost) ── */

const ttsSupported = typeof window !== "undefined" && typeof window.speechSynthesis !== "undefined";

const TTSButton = ({ text }) => {
  const [speaking, setSpeaking] = useState(false);
  const utterRef = useRef(null);

  useEffect(() => () => {
    // Cancel any in-flight utterance when this card unmounts
    if (utterRef.current && ttsSupported) window.speechSynthesis.cancel();
  }, []);

  if (!ttsSupported || !text || !text.trim()) return null;

  const toggle = (e) => {
    e.stopPropagation();
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.0;
    u.pitch = 1.0;
    u.lang = "en-US";
    u.onend   = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    utterRef.current = u;
    setSpeaking(true);
    // Some browsers queue — cancel first to ensure a fresh play
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  };

  return (
    <Tooltip title={speaking ? "Stop" : "Read aloud"} placement="top">
      <IconButton
        size="small"
        onClick={toggle}
        sx={{
          color: speaking ? "#7c3aed" : "#9CA3AF",
          p: 0.25,
          "&:hover": { color: "#7c3aed", backgroundColor: "#f5f3ff" },
        }}
      >
        {speaking ? <VolumeOffIcon sx={{ fontSize: 14 }} /> : <VolumeUpIcon sx={{ fontSize: 14 }} />}
      </IconButton>
    </Tooltip>
  );
};

/* ── Send-SMS button ── */

const SendSmsButton = ({ text, toPhone }) => {
  const [sending, setSending] = useState(false);
  if (!text || !text.trim() || !toPhone) return null;
  const click = async (e) => {
    e.stopPropagation();
    if (sending) return;
    const ok = window.confirm(`Send this note as SMS to ${toPhone}?`);
    if (!ok) return;
    try {
      setSending(true);
      const out = await apiSendSms({ toPhone, body: text });
      if (out?.status === "success") {
        window.alert(`Sent (sid: ${out.messageSid || "?"})`);
      } else {
        window.alert(`Failed: ${out?.error || "unknown error"}`);
      }
    } catch (err) {
      window.alert(`Failed: ${err?.message || String(err)}`);
    } finally {
      setSending(false);
    }
  };
  return (
    <Tooltip title={sending ? "Sending..." : `Send via SMS to ${toPhone}`}>
      <IconButton size="small" onClick={click} disabled={sending} sx={{ p: 0.25, color: sending ? "#9CA3AF" : "#0EA5E9", "&:hover": { color: "#0284C7" } }}>
        <SmsIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  );
};

/* ── Note card ── */

const NoteCard = ({ note, onDelete, onUpdate, smsPhone }) => {
  const meta = TYPE_META[note.type] || TYPE_META.text;
  const NoteIcon = meta.icon;
  const editable = note.type !== "audio" && note.type !== "image";
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(note.content || "");
  const startEdit = () => { setDraft(note.content || ""); setIsEditing(true); };
  const cancelEdit = () => { setDraft(note.content || ""); setIsEditing(false); };
  const saveEdit = () => {
    const next = (draft || "").trim();
    if (next === (note.content || "").trim()) { setIsEditing(false); return; }
    onUpdate?.(note.id, { content: next });
    setIsEditing(false);
  };
  return (
    <Box sx={{ border: "1px solid #E5E7EB", borderRadius: 1.5, p: 1.25, backgroundColor: "#fff" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <NoteIcon sx={{ fontSize: 13, color: meta.color }} />
          <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#6B7280", textTransform: "uppercase" }}>{meta.label}</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography sx={{ fontSize: 10, color: "#9CA3AF" }}>{formatTime(note.createdAt)}</Typography>
          {!isEditing && note.content && note.type !== "audio" && (
            <Gate flag="journal.card.readAloud"><TTSButton text={note.content} /></Gate>
          )}
          {!isEditing && note.content && note.type !== "audio" && smsPhone && (
            <Gate flag="journal.card.sms"><SendSmsButton text={note.content} toPhone={smsPhone} /></Gate>
          )}
          {!isEditing && editable && onUpdate && (
            <Gate flag="journal.card.edit">
            <IconButton size="small" onClick={startEdit} title="Edit note" sx={{ color: "#9CA3AF", p: 0.25, "&:hover": { color: "#3B82F6" } }}>
              <EditIcon sx={{ fontSize: 14 }} />
            </IconButton>
            </Gate>
          )}
          {!isEditing && (
            <Gate flag="journal.card.delete">
            <IconButton size="small" onClick={() => onDelete(note.id)} title="Delete note" sx={{ color: "#9CA3AF", p: 0.25, "&:hover": { color: "#EF4444" } }}>
              <DeleteIcon sx={{ fontSize: 14 }} />
            </IconButton>
            </Gate>
          )}
        </Box>
      </Box>
      {note.type === "image" && note.imageUrl && (
        <Box component="img" src={note.imageUrl} alt="Photo" sx={{ maxWidth: "100%", borderRadius: 1, mb: 0.5, maxHeight: 200, objectFit: "cover" }} />
      )}
      {note.type === "audio" && note.audioUrl && <AudioPlayer src={note.audioUrl} />}
      {note.type === "link" && note.url && !isEditing && (
        <Typography component="a" href={note.url} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 12, color: "#DB2777", textDecoration: "underline", wordBreak: "break-all" }}>{note.url}</Typography>
      )}
      {isEditing ? (
        <Box>
          <TextField
            fullWidth multiline minRows={2} autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { fontSize: 12 } }}
          />
          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end", mt: 0.5 }}>
            <Button size="small" onClick={cancelEdit} sx={{ fontSize: 11, textTransform: "none" }}>Cancel</Button>
            <Button size="small" variant="contained" onClick={saveEdit} sx={{ fontSize: 11, textTransform: "none" }}>Save</Button>
          </Box>
        </Box>
      ) : (
        note.content && (
          <Typography sx={{ fontSize: 12, color: "#334155", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{note.content}</Typography>
        )
      )}
    </Box>
  );
};

/* ── Reminder Form (sentence-style, matches admin) ── */

const INDIGO = "#4f46e5";
const INDIGO_DARK = "#3730a3";

function ReminderForm({ onSave, onCancel, contactName, initialData, isEdit }) {
  const initialAmount = initialData?.amount ?? initialData?.days ?? "";
  const [amount, setAmount] = useState(initialAmount);
  const [unit, setUnit] = useState(initialData?.unit || "day");
  const [action, setAction] = useState(initialData?.action || "");
  const [text, setText] = useState(initialData?.text || "");
  const [url, setUrl] = useState(initialData?.url || "");
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const isValid = amount && unit && action;

  const handleSave = async () => {
    if (!isValid) return;
    try {
      setIsSaving(true);
      await onSave({ amount: Number(amount), unit, action, text, url });
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") { e.preventDefault(); onCancel(); return; }
    if (e.key === "Enter") { e.preventDefault(); handleSave(); }
  };

  const inputSx = {
    fontSize: 13, fontWeight: 500, color: INDIGO_DARK,
    border: "none", borderBottom: `2px solid ${INDIGO}`,
    background: "transparent", outline: "none", padding: "2px 4px",
    fontFamily: "inherit",
  };

  const selectSx = {
    ...inputSx, cursor: "pointer",
    WebkitAppearance: "none", appearance: "none",
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%234f46e5' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat", backgroundPosition: "right 4px center", paddingRight: 20,
  };

  return (
    <Box sx={{ border: "1.5px dashed #c7d2fe", borderRadius: 1.5, p: 1.5, mb: 0.75, backgroundColor: "#f8fafc" }}>
      {/* Sentence row */}
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px", lineHeight: 2 }}>
        <Typography component="span" sx={{ fontSize: 13, color: "#475569" }}>
          {isEdit ? "Reminder in" : "Set a reminder in"}
        </Typography>
        <input
          ref={inputRef}
          type="number"
          min="1"
          max="365"
          placeholder="#"
          value={amount}
          onChange={(e) => setAmount(Math.max(0, Math.min(365, Number(e.target.value))) || "")}
          onKeyDown={handleKeyDown}
          style={{ ...inputSx, width: 48, textAlign: "center" }}
        />
        <select value={unit} onChange={(e) => setUnit(e.target.value)} onKeyDown={handleKeyDown} style={{ ...selectSx, minWidth: 80 }}>
          <option value="day">days</option>
          <option value="week">weeks</option>
          <option value="month">months</option>
          <option value="year">years</option>
        </select>
        <Typography component="span" sx={{ fontSize: 13, color: "#475569" }}>to</Typography>
        <select value={action} onChange={(e) => setAction(e.target.value)} onKeyDown={handleKeyDown} style={selectSx}>
          <option value="" disabled>select action</option>
          {ACTION_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </Box>

      {/* Details row */}
      <Box sx={{ mt: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {(action === "call" || action === "email") && contactName && (
            <Box component="span" sx={{
              fontSize: 13, fontWeight: 600, color: "#334155", backgroundColor: "#F1F5F9",
              px: 0.75, py: 0.25, borderBottom: "1.5px solid #cbd5e1", whiteSpace: "nowrap", flexShrink: 0,
            }}>
              {contactName}
            </Box>
          )}
          <input
            type="text"
            placeholder="Enter details..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{
              width: "100%", boxSizing: "border-box", flex: 1,
              fontSize: 13, fontFamily: "inherit", color: "#334155",
              border: "none", borderBottom: "1.5px solid #cbd5e1",
              background: "transparent", outline: "none", padding: "6px 0",
            }}
          />
        </Box>
        {(action === "attend_meeting" || action === "meet_in_person") && (
          <input
            type="url"
            placeholder="Enter url..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{
              width: "100%", boxSizing: "border-box", marginTop: 4,
              fontSize: 13, fontFamily: "inherit", color: "#334155",
              border: "none", borderBottom: "1.5px solid #cbd5e1",
              background: "transparent", outline: "none", padding: "6px 0",
            }}
          />
        )}
      </Box>

      {/* Actions */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.75 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={onCancel}
          sx={{
            px: 1.5, py: 0.5, borderRadius: "6px", textTransform: "none",
            fontSize: 12, fontWeight: 600, color: INDIGO,
            borderColor: INDIGO,
            "&:hover": { borderColor: "#4338ca", backgroundColor: "#EEF2FF" },
          }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          onClick={handleSave}
          disabled={!isValid || isSaving}
          sx={{
            px: 1.5, py: 0.5, borderRadius: "6px", textTransform: "none",
            fontSize: 12, fontWeight: 600, color: "#fff",
            backgroundColor: INDIGO, boxShadow: "0 2px 6px rgba(79,70,229,0.3)",
            "&:hover": { backgroundColor: "#4338ca" },
            "&.Mui-disabled": { opacity: 0.5, backgroundColor: INDIGO, color: "#fff" },
          }}
        >
          {isSaving ? "Saving..." : isEdit ? "Update" : "Set Reminder"}
        </Button>
      </Box>
    </Box>
  );
}

/* ── Reminder Card (display, matches admin without assignment) ── */

function ReminderCard({ reminder, contactName, contactReach, onEdit, onDelete, onMarkDone, onSelect, selected }) {
  const rawData = reminder.data;
  const data = typeof rawData === "string" ? (() => { try { return JSON.parse(rawData); } catch { return {}; } })() : (rawData || {});
  const { status } = reminder;
  const isDone = ["done", "completed"].includes(String(data?.status || "").trim().toLowerCase());
  const isTriggered = (status === "triggered" || reminder?.state === "due") && !isDone;
  const actionLabel = ACTION_OPTIONS.find((a) => a.value === data?.action)?.label || data?.action || "";
  const rawState = String(reminder?.state || "").toLowerCase();
  const stateValue = rawState === "active" ? "scheduled" : (rawState || "created");
  const displayState = (isTriggered || stateValue === "due") ? "due" : stateValue;
  const chipStyle = STATE_CHIP_STYLES[displayState] || STATE_CHIP_STYLES.created;
  const dateLabel = formatTimestamp(reminder?.dueDate || reminder?.timestamp);
  const doneStyle = {};

  const contactEmail = contactReach?.emails?.[0]?.value || data?.contactEmail || "";
  const contactPhone = contactReach?.phones?.[0]?.value || data?.contactPhone || "";

  return (
    <Box onClick={() => onSelect && onSelect(reminder)} onDoubleClick={onEdit} sx={{
      border: "1px solid", borderRadius: 1.5, p: 1.25, backgroundColor: "#fff", cursor: "pointer",
      borderColor: selected ? "#7c3aed" : isTriggered ? "#fbbf24" : isDone ? "#E5E7EB" : "#d1d5db",
      ...(selected ? { boxShadow: "0 0 0 2px rgba(124,58,237,0.25)", backgroundColor: "#faf5ff" } : {}),
      ...(isTriggered && !selected ? { boxShadow: "0 2px 8px rgba(251,191,36,0.15)", backgroundColor: "#fffdf5" } : {}),
    }}>
      {/* Top row: state chip + date + action pill + contact name + delete */}
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, mb: 0.5 }}>
        <Box sx={{
          display: "inline-flex", alignItems: "center", px: 1, py: 0.125,
          borderRadius: "999px", border: "1px solid", fontSize: 11, fontWeight: 700,
          ...chipStyle, textTransform: "capitalize", whiteSpace: "nowrap", flexShrink: 0,
        }}>
          {displayState === "due" && <ReminderIcon sx={{ fontSize: 12, mr: 0.25, color: "#166534" }} />}
          {displayState === "due" ? "Due" : displayState}
        </Box>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: INDIGO_DARK, flex: 1, ...doneStyle }}>
          {dateLabel || "No date set"}
          {data?.contactName && (
            <>
              <Typography component="span" sx={{ fontSize: 13, color: "#475569", ...doneStyle }}> — </Typography>
              {data?.action === "call" && contactPhone ? (
                <Gate flag="journal.reminder.call">
                <Button size="small" component="span" startIcon={<PhoneIcon sx={{ fontSize: 14 }} />}
                  onClick={(e) => { e.stopPropagation(); window.location.href = `tel:${contactPhone}`; }}
                  sx={{
                    minWidth: "auto", height: 20, px: 0.75, borderRadius: "999px", textTransform: "none",
                    fontSize: 11, fontWeight: 700, color: "#047857", backgroundColor: "#ECFDF5",
                    border: "1px solid #A7F3D0", "& .MuiButton-startIcon": { mr: 0.3 },
                    "&:hover": { backgroundColor: "#D1FAE5" }, verticalAlign: "middle", mx: 0.5,
                  }}>
                  Call
                </Button>
                </Gate>
              ) : data?.action === "text" && contactPhone ? (
                <Gate flag="journal.reminder.text">
                <Button size="small" component="span" startIcon={<SmsIcon sx={{ fontSize: 14 }} />}
                  onClick={(e) => { e.stopPropagation(); window.location.href = `sms:${contactPhone}`; }}
                  sx={{
                    minWidth: "auto", height: 20, px: 0.75, borderRadius: "999px", textTransform: "none",
                    fontSize: 11, fontWeight: 700, color: "#1E40AF", backgroundColor: "#EFF6FF",
                    border: "1px solid #93C5FD", "& .MuiButton-startIcon": { mr: 0.3 },
                    "&:hover": { backgroundColor: "#DBEAFE" }, verticalAlign: "middle", mx: 0.5,
                  }}>
                  Text
                </Button>
                </Gate>
              ) : data?.action === "email" && contactEmail ? (
                <Gate flag="journal.reminder.email">
                <Button size="small" component="span" startIcon={<EmailIcon sx={{ fontSize: 14 }} />}
                  onClick={(e) => { e.stopPropagation(); window.location.href = `mailto:${contactEmail}`; }}
                  sx={{
                    minWidth: "auto", height: 20, px: 0.75, borderRadius: "999px", textTransform: "none",
                    fontSize: 11, fontWeight: 700, color: "#1E40AF", backgroundColor: "#EFF6FF",
                    border: "1px solid #93C5FD", "& .MuiButton-startIcon": { mr: 0.3 },
                    "&:hover": { backgroundColor: "#DBEAFE" }, verticalAlign: "middle", mx: 0.5,
                  }}>
                  Email
                </Button>
                </Gate>
              ) : actionLabel ? (
                <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, color: INDIGO_DARK, mx: 0.5, ...doneStyle }}>{actionLabel}</Typography>
              ) : null}
              <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: INDIGO_DARK, ...doneStyle }}>{data.contactName}</Typography>
            </>
          )}
        </Typography>
        {!isDone && (
          <Gate flag="journal.reminder.markDone">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onMarkDone(); }} title="Mark as done" sx={{ color: "#9CA3AF", p: 0.25, "&:hover": { color: "#10b981" }, flexShrink: 0 }}>
            <DoneIcon sx={{ fontSize: 14 }} />
          </IconButton>
          </Gate>
        )}
        <Gate flag="journal.reminder.delete">
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(); }} title="Delete" sx={{ color: "#9CA3AF", p: 0.25, "&:hover": { color: "#EF4444" }, flexShrink: 0 }}>
          <DeleteIcon sx={{ fontSize: 14 }} />
        </IconButton>
        </Gate>
      </Box>

      {/* Details text */}
      {data?.text && (
        <Typography sx={{ fontSize: 12, color: "#64748b", lineHeight: 1.5, mt: 0.5, ...doneStyle }}>{data.text}</Typography>
      )}
    </Box>
  );
}

/* ══════════════════════════════════════════
   Main panel
   ══════════════════════════════════════════ */

export default function JournalPanel({
  contact,
  refId: refIdProp,
  refCollection = "contacts",
  refName: refNameProp,
  refReach: refReachProp,
  emptyMessage,
  authorId,
  onClose,
  refreshKey,
  selectedReminderId,
  onSelectReminder,
  // ── GLOBAL / contact-free mode (used by the standalone PageJournal) ──
  // When `scope` is set (e.g. "global") the panel stamps scope/domain +
  // provenance into every write and there is no contact, so contact-only
  // actions (send-via-SMS, call/text/email reminder pills) auto-hide because
  // they are already keyed off a contact phone/email that is absent.
  // These props are all optional and no-ops in the default contact mode.
  scope,
  domain,
  provenanceKey,
  provenanceSource,
  footerLeftLabel,
  // When true the in-panel icon toolbar is hidden (the standalone Journal page
  // surfaces the same actions in the AppBar instead — avoids duplication).
  hideToolbar = false,
}) {
  const [journal, setJournal] = useState(null);
  const [notes, setNotes] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [noteType, setNoteType] = useState("text");
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeechActive, setIsSpeechActive] = useState(false);
  const [isTtsActive, setIsTtsActive] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [filterTypes, setFilterTypes] = useState(new Set(ALL_TYPES));
  // Which add-entry dialog is open: null | "note" | "link" | "reminder".
  // Photo/audio/speech/speak stay inline (native picker / capture toggles).
  const [addDialog, setAddDialog] = useState(null);
  const [editingReminderId, setEditingReminderId] = useState(null);
  const listRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const speechRecRef = useRef(null);
  const toolbarTtsRef = useRef(null);
  const fileInputRef = useRef(null);

  // Active domain for SILENT journal isolation (never rendered). Prefer the
  // explicit `domain` prop (global mode already passes the active domain), else
  // fall back to the signed-in / super-admin-selected domain in Redux. "All"
  // resolves to "" so cross-domain viewing stays unscoped.
  const reduxDomain = useSelector((state) => state.auth.user?.domain || "");
  const scopeDomain = useMemo(() => {
    const d = String(domain || reduxDomain || "").trim();
    return d && d !== "All" ? d : "";
  }, [domain, reduxDomain]);

  const contactId = refIdProp || contact?.id;
  const contactName = refNameProp != null
    ? refNameProp
    : (() => {
        const p = contact?.identity?.person || {};
        return `${p.firstName || ""} ${p.lastName || ""}`.trim();
      })();
  const contactReach = useMemo(
    () => refReachProp || contact?.reach || {},
    [refReachProp, contact?.reach]
  );
  const smsPhone = useMemo(() => {
    const phones = Array.isArray(contactReach?.phones) ? contactReach.phones : [];
    if (!phones.length) return "";
    const primary = phones.find((p) => p?.isPrimary && p?.value) || phones.find((p) => p?.value);
    return primary?.value ? String(primary.value).trim() : "";
  }, [contactReach]);

  const isCaptureMode = isRecording || isSpeechActive;
  const isFiltered = filterTypes.size < ALL_TYPES.length;
  const ttsSupported = typeof window !== "undefined" && typeof window.speechSynthesis !== "undefined";
  const toolbarTtsText = useMemo(() => {
    if (noteType === "link") return String(linkUrl || "").trim();
    return String(newNote || "").trim();
  }, [noteType, linkUrl, newNote]);
  const latestReadableNoteText = useMemo(() => {
    const firstTextNote = (notes || []).find(
      (n) => n?.type !== "audio" && String(n?.content || "").trim()
    );
    return String(firstTextNote?.content || "").trim();
  }, [notes]);

  /* ── mix notes + reminders into one sorted list ── */
  const mixedItems = useMemo(() => {
    const noteItems = notes.map((n) => ({ ...n, _kind: "note", _sortTime: n.createdAt || 0 }));
    const reminderItems = reminders.map((r) => ({
      ...r,
      _kind: "reminder",
      type: "reminder",
      _sortTime: new Date(r.createdAt || 0).getTime(),
    }));
    const all = [...noteItems, ...reminderItems];
    all.sort((a, b) => {
      const ta = typeof a._sortTime === "number" ? a._sortTime : new Date(a._sortTime).getTime();
      const tb = typeof b._sortTime === "number" ? b._sortTime : new Date(b._sortTime).getTime();
      return tb - ta;
    });
    return all;
  }, [notes, reminders]);

  const filteredItems = isFiltered
    ? mixedItems.filter((item) => filterTypes.has(item.type))
    : mixedItems;

  const totalCount = mixedItems.length;

  /* ── load journal + reminders ── */
  const loadJournal = useCallback(async () => {
    if (!contactId || !authorId) return;
    setLoading(true);
    try {
      const [journalRes, reminderRes] = await Promise.all([
        apiGetJournals({ authorId, refId: contactId, refCollection, type: "journal", ...(scopeDomain ? { domain: scopeDomain } : {}) }),
        apiGetJournals({ authorId, refId: contactId, refCollection, type: "reminder", ...(scopeDomain ? { domain: scopeDomain } : {}) }),
      ]);
      const j = journalRes?.journals?.[0] || null;
      setJournal(j);
      setNotes(parseNotes(j));
      setReminders((reminderRes?.journals || []).map((r) => ({ ...r, timestamp: r.dueDate })));
    } catch (e) {
      console.error("Failed to load journal:", e);
    } finally {
      setLoading(false);
    }
    // refreshKey is not read inside — it is an external reload trigger (chat
    // writes bump it so this list refetches).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, authorId, refreshKey, refCollection, scopeDomain]);

  useEffect(() => { loadJournal(); }, [loadJournal]);

  /* ── data-layer decoration (scope / domain / provenance) ──
     No-op in the default contact mode (all inputs undefined); in global mode
     it stamps data.scope, data.domain (silent, never rendered) and a
     data.<provenanceKey> audit block on every write. */
  const buildData = useCallback((base, action) => {
    const out = { ...base };
    if (scope) out.scope = scope;
    if (domain) out.domain = domain;
    if (provenanceKey && provenanceSource) {
      out[provenanceKey] = {
        source: provenanceSource,
        ...(scope ? { scope } : {}),
        updatedAt: new Date().toISOString(),
        action,
        authorId: String(authorId || ""),
      };
    }
    return out;
  }, [scope, domain, provenanceKey, provenanceSource, authorId]);

  /* ── persist notes ── */
  const persistNotes = useCallback(async (updatedNotes) => {
    if (!contactId || !authorId) return;
    try {
      const input = { authorId, type: "journal", ref: { id: contactId, collection: refCollection }, data: buildData({ notes: updatedNotes }, "upsert-notes") };
      if (scopeDomain) input.domain = scopeDomain;
      if (journal?.id) input.id = journal.id;
      const res = await apiUpsertJournal(input);
      if (res?.journal) setJournal(res.journal);
    } catch (e) {
      console.error("Failed to save journal:", e);
    }
  }, [contactId, authorId, journal, refCollection, buildData, scopeDomain]);

  /* ── add note ── */
  const addNote = useCallback((noteData) => {
    const note = { id: makeNoteId(), createdAt: Date.now(), authorId, ...noteData };
    const updated = [note, ...notes];
    setNotes(updated);
    persistNotes(updated);
    setTimeout(() => listRef.current?.scrollTo({ top: 0, behavior: "smooth" }), 50);
  }, [notes, authorId, persistNotes]);

  const handleAddTextNote = useCallback(() => {
    const text = newNote.trim();
    if (!text) return;
    addNote({ type: "text", content: text });
    setNewNote("");
    setAddDialog(null);
  }, [newNote, addNote]);

  const handleAddLink = useCallback(() => {
    const url = linkUrl.trim();
    if (!url) return;
    addNote({ type: "link", url, content: url });
    setLinkUrl("");
    setNoteType("text");
    setAddDialog(null);
  }, [linkUrl, addNote]);

  const handleDeleteNote = useCallback((noteId) => {
    const updated = notes.filter((n) => n.id !== noteId);
    setNotes(updated);
    persistNotes(updated);
  }, [notes, persistNotes]);

  const handleUpdateNote = useCallback((noteId, patch) => {
    const updated = notes.map((n) => n.id === noteId ? { ...n, ...patch, updatedAt: Date.now() } : n);
    setNotes(updated);
    persistNotes(updated);
  }, [notes, persistNotes]);

  /* ── reminder CRUD ── */
  const handleCreateReminder = useCallback(async (formData) => {
    try {
      const triggerDate = setTimeTo8am(addDuration(new Date(), formData.amount, formData.unit)).toISOString();
      const res = await apiCreateJournal({
        authorId,
        ref: { collection: refCollection, id: contactId },
        type: "reminder",
        dueDate: triggerDate,
        ...(scopeDomain ? { domain: scopeDomain } : {}),
        data: buildData({
          amount: formData.amount,
          unit: formData.unit,
          days: formData.unit === "day" ? formData.amount : undefined,
          action: formData.action,
          contactName,
          text: formData.text,
          url: formData.url,
        }, "create-reminder"),
      });
      const saved = res?.journal;
      if (saved) {
        saved.timestamp = saved.dueDate;
        setReminders((prev) => [saved, ...prev.filter((r) => r.id !== saved.id)]);
      }
      setAddDialog(null);
    } catch (e) {
      console.error("Failed to create reminder:", e);
    }
  }, [authorId, contactId, contactName, refCollection, buildData, scopeDomain]);

  const handleUpdateReminder = useCallback(async (id, formData) => {
    try {
      const triggerDate = setTimeTo8am(addDuration(new Date(), formData.amount, formData.unit)).toISOString();
      const res = await apiUpdateJournal({
        id,
        dueDate: triggerDate,
        ...(scopeDomain ? { domain: scopeDomain } : {}),
        data: buildData({
          amount: formData.amount,
          unit: formData.unit,
          days: formData.unit === "day" ? formData.amount : undefined,
          action: formData.action,
          contactName,
          text: formData.text,
          url: formData.url,
        }, "update-reminder"),
      });
      const saved = res?.journal;
      if (saved) {
        saved.timestamp = saved.dueDate;
        setReminders((prev) => prev.map((r) => r.id === saved.id ? saved : r));
      }
      setEditingReminderId(null);
    } catch (e) {
      console.error("Failed to update reminder:", e);
    }
  }, [contactName, buildData, scopeDomain]);

  const handleMarkDone = useCallback(async (id) => {
    try {
      await apiCancelJournalReminder(id);
      // Find the reminder to get existing data
      const reminder = reminders.find((r) => r.id === id);
      const existData = typeof reminder?.data === "string" ? JSON.parse(reminder.data) : (reminder?.data || {});
      const doneData = buildData({ ...existData, status: "done" }, "mark-done");
      const persisted = await apiUpdateJournal({ id, data: doneData });
      const updated = persisted?.journal || { ...reminder, data: doneData };
      setReminders((prev) => prev.map((r) => r.id === id ? { ...r, ...updated, data: doneData } : r));
    } catch (e) {
      console.error("Failed to mark reminder as done:", e);
    }
  }, [reminders, buildData]);

  const handleDeleteReminder = useCallback(async (id) => {
    try {
      await apiDeleteJournal(id);
      setReminders((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      console.error("Failed to delete reminder:", e);
    }
  }, []);

  /* ── photo capture ── */
  const handlePhotoClick = () => { fileInputRef.current?.click(); };
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { addNote({ type: "image", imageUrl: reader.result, content: file.name }); };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  /* ── audio recording ── */
  const toggleRecording = async () => {
    if (isRecording) { mediaRecorderRef.current?.stop(); setIsRecording(false); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const url = URL.createObjectURL(blob);
        addNote({ type: "audio", audioUrl: url, content: "Audio recording" });
        stream.getTracks().forEach((t) => t.stop());
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
    } catch (err) { console.error("Microphone access denied:", err); }
  };

  /* ── speech-to-text ── */
  const toggleSpeech = () => {
    if (isSpeechActive) {
      speechRecRef.current?.stop();
      setIsSpeechActive(false);
      setLiveTranscript("");
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { console.error("Speech recognition not supported"); return; }
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";
    recognition.onresult = (event) => {
      let interim = "";
      let final = "";
      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          final += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }
      if (final.trim()) {
        addNote({ type: "text", content: final.trim() });
        setLiveTranscript("");
      } else {
        setLiveTranscript(interim);
      }
    };
    recognition.onerror = () => { setIsSpeechActive(false); setLiveTranscript(""); };
    recognition.onend = () => { setIsSpeechActive(false); setLiveTranscript(""); };
    recognition.start();
    speechRecRef.current = recognition;
    setIsSpeechActive(true);
  };

  const toggleToolbarTts = useCallback(() => {
    if (!ttsSupported) return;
    if (isTtsActive) {
      window.speechSynthesis.cancel();
      setIsTtsActive(false);
      return;
    }
    const textToSpeak = toolbarTtsText || latestReadableNoteText;
    if (!textToSpeak) return;
    const u = new SpeechSynthesisUtterance(textToSpeak);
    u.rate = 1.0;
    u.pitch = 1.0;
    u.lang = "en-US";
    u.onend = () => setIsTtsActive(false);
    u.onerror = () => setIsTtsActive(false);
    toolbarTtsRef.current = u;
    setIsTtsActive(true);
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  }, [isTtsActive, toolbarTtsText, latestReadableNoteText, ttsSupported]);

  useEffect(() => () => {
    if (ttsSupported) window.speechSynthesis.cancel();
  }, [ttsSupported]);

  /* ── open an add-entry dialog (note / link / reminder) ── */
  const openAddDialog = (kind) => {
    if (kind === "reminder") {
      setEditingReminderId(null);
      setNoteType("reminder");
    } else if (kind === "link") {
      setNoteType("link");
      setLinkUrl("");
    } else {
      setNoteType("text");
      setNewNote("");
    }
    setAddDialog(kind);
  };
  const closeAddDialog = () => { setAddDialog(null); setNoteType("text"); };

  /* Enter-to-save inside the single-line Link dialog field. */
  const handleLinkKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); handleAddLink(); }
  };

  /* ── shared toolbar action handlers (used by the in-panel toolbar AND, in
     standalone mode, by the AppBar journal buttons via JOURNAL_ACTION_EVENT) ── */

  // Ref always holds the latest dispatcher so the window listener stays stable
  // while still calling handlers that close over current state.
  const journalActionRef = useRef(null);
  journalActionRef.current = (detail) => {
    switch (detail?.action) {
      case "reminder": openAddDialog("reminder"); break;
      case "note": openAddDialog("note"); break;
      case "photo": handlePhotoClick(); break;
      case "audio": toggleRecording(); break;
      case "speech": toggleSpeech(); break;
      case "speak": toggleToolbarTts(); break;
      case "link": openAddDialog("link"); break;
      default: break;
    }
  };

  // Only the standalone/global journal (scope set) responds to AppBar actions.
  useEffect(() => {
    if (!scope) return undefined;
    const handler = (e) => journalActionRef.current?.(e.detail || {});
    window.addEventListener(JOURNAL_ACTION_EVENT, handler);
    return () => window.removeEventListener(JOURNAL_ACTION_EVENT, handler);
  }, [scope]);

  /* Type filter: compact multi-select dropdown in the header title row (same
     styling as the Contacts category filter). Reuses the shared filterTypes /
     filteredItems mechanism — no second filter. */
  const filterCheckboxes = (
    <Gate flag="journal.filter">
      <FormControl size="small" sx={{ minWidth: 96, ml: "auto", mr: 0.5 }}>
        <Select
          multiple
          value={Array.from(filterTypes)}
          onChange={(e) => {
            const vals = Array.isArray(e.target.value) ? e.target.value : [];
            // Unchecking the last type resets to "all" (same as the old checkboxes).
            setFilterTypes(vals.length ? new Set(vals) : new Set(ALL_TYPES));
          }}
          displayEmpty
          renderValue={() =>
            isFiltered ? (
              <span style={{ fontSize: 12 }}>
                {ALL_TYPES.filter((t) => filterTypes.has(t)).map((t) => TYPE_META[t].label).join(", ")}
              </span>
            ) : (
              <span style={{ fontSize: 12, color: "#9CA3AF" }}>All types</span>
            )
          }
          sx={{
            height: 26,
            fontSize: 12,
            borderRadius: 1,
            backgroundColor: "#fff",
            maxWidth: 180,
            "& .MuiSelect-select": { py: 0.25, pr: "24px !important" },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#D1D5DB" },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#9CA3AF" },
          }}
        >
          {ALL_TYPES.map((type) => {
            const meta = TYPE_META[type];
            return (
              <MenuItem key={type} value={type} sx={{ fontSize: 12, py: 0.25 }}>
                <Checkbox
                  size="small"
                  checked={filterTypes.has(type)}
                  sx={{ p: 0.375, mr: 0.5, color: meta.color, "&.Mui-checked": { color: meta.color } }}
                />
                {meta.label}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Gate>
  );

  const headerActions = !contactId ? null : hideToolbar ? null : (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.25 }}>
      <Gate flag="journal.addReminder"><ActionBtn title="Add reminder" label="Reminder" onClick={() => openAddDialog("reminder")} active={addDialog === "reminder"} disabled={isCaptureMode} accent={ACCENTS.reminder} Icon={ReminderIcon} /></Gate>
      <Gate flag="journal.addText"><ActionBtn title="Add text" label="Note" onClick={() => openAddDialog("note")} active={addDialog === "note"} disabled={isCaptureMode} accent={ACCENTS.text} Icon={TextFieldsIcon} /></Gate>
      <Gate flag="journal.photo"><ActionBtn title="Photo" label="Photo" onClick={handlePhotoClick} active={false} disabled={isCaptureMode} accent={ACCENTS.photo} Icon={PhotoCameraIcon} /></Gate>
      <Gate flag="journal.audio"><ActionBtn title={isRecording ? "Stop recording" : "Record audio"} label="Audio" onClick={toggleRecording} active={isRecording} disabled={isCaptureMode && !isRecording} accent={ACCENTS.audio} ActiveIcon={StopIcon} Icon={AudioRecordIcon} /></Gate>
      <Gate flag="journal.speechToText"><ActionBtn title={isSpeechActive ? "Stop" : "Speech to text"} label="Speech" onClick={toggleSpeech} active={isSpeechActive} disabled={isCaptureMode && !isSpeechActive} accent={ACCENTS.text} ActiveIcon={StopIcon} Icon={SpeechIcon} /></Gate>
      <Gate flag="journal.textToSpeech"><ActionBtn
        title={isTtsActive ? "Stop text to speech" : "Text to speech"}
        label="Speak"
        onClick={toggleToolbarTts}
        active={isTtsActive}
        disabled={!ttsSupported || (isCaptureMode && !isTtsActive)}
        accent={ACCENTS.text}
        ActiveIcon={VolumeOffIcon}
        Icon={VolumeUpIcon}
      /></Gate>
      <Gate flag="journal.addLink"><ActionBtn title="Add link" label="Link" onClick={() => openAddDialog("link")} active={addDialog === "link"} disabled={isCaptureMode} accent={ACCENTS.link} Icon={LinkIcon} /></Gate>
    </Box>
  );

  return (
    <AddressbookPanelShell
      onClose={onClose}
      titleContent={
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", flex: 1, gap: 0.5 }}>
          <JournalIcon sx={{ fontSize: 16, color: "#D97706" }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>Journal</Typography>
          {contactId ? filterCheckboxes : null}
        </Box>
      }
      headerActions={headerActions}
      footerLeft={
        footerLeftLabel !== undefined ? (
          footerLeftLabel ? (
            <Typography sx={{ color: "text.secondary", fontSize: 11 }}>{footerLeftLabel}</Typography>
          ) : null
        ) : contactId ? (
          <Typography sx={{ color: "text.secondary", fontSize: 11 }}>
            {contactName || contactId}
          </Typography>
        ) : null
      }
      footerRight={
        <Typography sx={{ color: "text.secondary", fontSize: 11 }}>
          {filteredItems.length}
          {isFiltered ? ` of ${totalCount}` : ""} entr{filteredItems.length === 1 ? "y" : "ies"}
        </Typography>
      }
    >
      {!contactId ? (
        <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
          <Typography sx={{ color: "text.secondary", fontSize: 13 }}>{emptyMessage || "Select a contact to view journal"}</Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
          {/* ── Capture status area (add-entry inputs now live in a dialog) ── */}
          {isCaptureMode && (
          <Box sx={{ px: 1, pt: 1, pb: 0.75, borderBottom: "1px solid #E5E7EB", flexShrink: 0 }}>
            {/* Recording indicator */}
            {isRecording && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, py: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#DC2626", animation: "pulse 1.5s infinite", "@keyframes pulse": { "0%, 100%": { opacity: 1 }, "50%": { opacity: 0.3 } } }} />
                <Typography sx={{ fontSize: 12, color: "#DC2626", fontWeight: 600 }}>Recording...</Typography>
                <IconButton size="small" onClick={toggleRecording} sx={{ color: "#DC2626", ml: "auto" }}><StopIcon sx={{ fontSize: 18 }} /></IconButton>
              </Box>
            )}

            {/* Speech indicator */}
            {isSpeechActive && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, py: 0.5, flexWrap: "wrap" }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#059669", animation: "pulse 1.5s infinite", "@keyframes pulse": { "0%, 100%": { opacity: 1 }, "50%": { opacity: 0.3 } }, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 12, color: "#059669", fontWeight: 600, flex: 1, minWidth: 0 }}>
                  {liveTranscript || "Listening..."}
                </Typography>
                <IconButton size="small" onClick={toggleSpeech} sx={{ color: "#059669", flexShrink: 0 }}><StopIcon sx={{ fontSize: 18 }} /></IconButton>
              </Box>
            )}
          </Box>
          )}

          {/* Hidden file input */}
          <input ref={fileInputRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={handleFileChange} />

          {/* ── Add-entry dialogs (note / link / reminder) ──
              Triggered by the in-panel toolbar (contact mode) AND the AppBar
              journal actions (standalone mode) via the same openAddDialog. */}
          <Dialog open={addDialog === "note"} onClose={closeAddDialog} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontSize: 15, fontWeight: 600, color: INDIGO_DARK, pb: 1 }}>Add note</DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <TextField
                autoFocus fullWidth multiline minRows={4}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a note..."
                sx={{ mt: 0.5, "& .MuiInputBase-root": { fontSize: 13 } }}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={closeAddDialog} sx={{ textTransform: "none", color: "#64748b" }}>Cancel</Button>
              <Button
                variant="contained" onClick={handleAddTextNote} disabled={!newNote.trim()}
                sx={{ textTransform: "none", backgroundColor: INDIGO, "&:hover": { backgroundColor: "#4338ca" } }}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={addDialog === "link"} onClose={closeAddDialog} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontSize: 15, fontWeight: 600, color: INDIGO_DARK, pb: 1 }}>Add link</DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <TextField
                autoFocus fullWidth type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={handleLinkKeyDown}
                placeholder="Paste a URL..."
                sx={{ mt: 0.5, "& .MuiInputBase-root": { fontSize: 13 } }}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={closeAddDialog} sx={{ textTransform: "none", color: "#64748b" }}>Cancel</Button>
              <Button
                variant="contained" onClick={handleAddLink} disabled={!linkUrl.trim()}
                sx={{ textTransform: "none", backgroundColor: INDIGO, "&:hover": { backgroundColor: "#4338ca" } }}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>

          {/* Reminder reuses the existing ReminderForm markup/logic verbatim;
              it supplies its own Cancel / Set Reminder buttons. */}
          <Dialog open={addDialog === "reminder"} onClose={closeAddDialog} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontSize: 15, fontWeight: 600, color: INDIGO_DARK, pb: 1 }}>Add reminder</DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <ReminderForm
                onSave={handleCreateReminder}
                onCancel={closeAddDialog}
                contactName={contactName}
              />
            </DialogContent>
          </Dialog>

          {/* ── Mixed list ── */}
          <Box ref={listRef} sx={{ flex: 1, overflow: "auto", p: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
            {loading && (
              <Typography sx={{ fontSize: 12, color: "#9CA3AF", textAlign: "center", py: 2 }}>Loading journal...</Typography>
            )}
            {!loading && filteredItems.length === 0 && (
              <Typography sx={{ fontSize: 12, color: "#9CA3AF", textAlign: "center", py: 4 }}>
                {isFiltered ? "No entries match the filter." : "No journal entries yet."}
              </Typography>
            )}
            {filteredItems.map((item) => {
              if (item._kind === "reminder") {
                if (editingReminderId === item.id) {
                  return (
                    <ReminderForm
                      key={item.id}
                      isEdit
                      initialData={item.data}
                      contactName={item.data?.contactName || contactName}
                      onSave={(formData) => handleUpdateReminder(item.id, formData)}
                      onCancel={() => setEditingReminderId(null)}
                    />
                  );
                }
                return (
                  <ReminderCard
                    key={item.id}
                    reminder={item}
                    contactName={contactName}
                    contactReach={contactReach}
                    selected={item.id === selectedReminderId}
                    onSelect={onSelectReminder}
                    onEdit={() => { setEditingReminderId(item.id); setAddDialog(null); }}
                    onDelete={() => handleDeleteReminder(item.id)}
                    onMarkDone={() => handleMarkDone(item.id)}
                  />
                );
              }
              return <NoteCard key={item.id} note={item} onDelete={handleDeleteNote} onUpdate={handleUpdateNote} smsPhone={smsPhone} />;
            })}
          </Box>
        </Box>
      )}
    </AddressbookPanelShell>
  );
}
